import { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormLabel,
  LinearProgress,
  LinearProgressProps,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';

export interface Props {
  label: string;
  helperText?: string;
  hideFormLabel?: boolean;
  varies?: boolean;
  loading?: boolean;
  loadingText?: string;
  error?: boolean;
  errorText?: string;
  linearProgressProps?: LinearProgressProps;
  children: ReactNode;
}

export const AppFormControl: FC<Props> = ({
  label,
  helperText = '',
  hideFormLabel,
  children,
  varies,
  loading,
  loadingText,
  error,
  errorText,
  linearProgressProps,
}) => {
  const { t } = useTranslation();
  const smallScreenSize = useMediaQuery<Theme>(({ breakpoints }) =>
    breakpoints.down('md'),
  );

  const tooltip = useMemo<string>(() => {
    if (loading && loadingText) return loadingText;
    return helperText;
  }, [helperText, loading, loadingText]);

  return (
    <Tooltip
      title={tooltip}
      placement={smallScreenSize ? 'top' : 'right'}
      arrow
    >
      <FormControl variant="standard" error={error} fullWidth>
        {!hideFormLabel && (
          <FormLabel id={`${label}-label`} htmlFor={`${label}-form`}>
            {label}
            {varies && (
              <Typography
                component="span"
                color="warning.main"
                fontWeight={100}
              >
                {` (${t('varies')})`}
              </Typography>
            )}
          </FormLabel>
        )}
        {!loading ? children : <LinearProgress {...linearProgressProps} />}
        {error && errorText && (
          <Typography variant="body2" color="error">
            {errorText}
          </Typography>
        )}
      </FormControl>
    </Tooltip>
  );
};
